import {
    BookOpen,
    HandHeart,
    Users,
    Leaf,
    Sprout,
    Sparkles,
    Heart,
    Stethoscope,
} from "lucide-react";
import Logo from "@/components/Logo";
import DownloadButtons from "@/components/DownloadButtons";
import BotanicalDivider from "@/components/BotanicalDivider";

const PRINCIPLES = [
    {
        icon: BookOpen,
        title: "Evidencia científica",
        text: "Información actualizada y rigurosa, alineada con las guías clínicas más recientes.",
    },
    {
        icon: HandHeart,
        title: "Respeto a la autonomía",
        text: "Cada mujer es la protagonista de sus decisiones. Aquí solo acompañamos.",
    },
    {
        icon: Users,
        title: "Decisiones compartidas",
        text: "Información clara para que dialogues con tu matrona y tu equipo sanitario.",
    },
    {
        icon: Sprout,
        title: "Parto respetado",
        text: "Reconocemos el parto como un proceso fisiológico, íntimo y profundamente personal.",
    },
    {
        icon: Leaf,
        title: "Lactancia informada",
        text: "Apoyo basado en evidencia, sin presiones ni juicios, respetando cada camino.",
    },
    {
        icon: Sparkles,
        title: "Acompañamiento continuo",
        text: "Disponible cuando lo necesites: de día, de madrugada, en cualquier momento.",
    },
    {
        icon: Stethoscope,
        title: "Continuidad de cuidados",
        text: "Una presencia constante a lo largo del embarazo, el parto y la crianza temprana.",
    },
    {
        icon: Heart,
        title: "Humanidad antes que tecnología",
        text: "La tecnología es el medio. El acompañamiento humano es el sentido.",
    },
];

export default function Home() {
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative overflow-hidden"
            >
                <div className="floral-veil absolute inset-0" aria-hidden="true" />
                <div className="relative max-w-5xl mx-auto px-6 sm:px-8 pt-14 sm:pt-20 pb-24 sm:pb-28">
                    <div className="fade-up">
                        <Logo size={88} withWordmark={false} className="mb-7" />
                    </div>
                    <p
                        className="eyebrow fade-up fade-up-delay-1"
                        data-testid="hero-eyebrow"
                    >
                        Bienvenida a Matrona IA
                    </p>
                    <h1
                        data-testid="hero-title"
                        className="headline mt-5 text-4xl sm:text-5xl lg:text-6xl max-w-3xl fade-up fade-up-delay-2"
                    >
                        Tu acompañamiento basado en evidencia,
                        <em className="italic text-[#b7677a] font-light">
                            {" "}
                            lleno de humanidad.
                        </em>
                    </h1>
                    <p
                        data-testid="hero-subtitle"
                        className="mt-6 text-base sm:text-lg leading-relaxed text-[#5c5450] max-w-2xl fade-up fade-up-delay-3"
                    >
                        Acompañamiento durante el embarazo, el parto, el
                        posparto, la lactancia y los primeros pasos de la
                        crianza.
                    </p>

                    <div className="mt-10 fade-up fade-up-delay-4">
                        <DownloadButtons />
                    </div>

                    <p className="mt-5 text-[0.85rem] text-[#8a807b] italic fade-up fade-up-delay-4">
                        Matrona IA ya está disponible para iPhone, iPad y
                        Android.
                    </p>
                </div>
            </section>

            {/* ¿QUÉ ES MATRONA IA? */}
            <section
                data-testid="what-is-section"
                className="px-6 sm:px-8 py-20 sm:py-28"
            >
                <div className="max-w-5xl mx-auto grid md:grid-cols-12 gap-10 items-start">
                    <div className="md:col-span-5">
                        <p className="eyebrow">Sobre nosotras</p>
                        <h2 className="headline mt-4 text-3xl sm:text-4xl lg:text-5xl">
                            Una matrona disponible cuando la
                            <em className="italic text-[#b7677a] font-light">
                                {" "}
                                necesites.
                            </em>
                        </h2>
                    </div>
                    <div className="md:col-span-7 space-y-5 text-[1.02rem] leading-relaxed text-[#5c5450]">
                        <p>
                            Matrona IA es una inteligencia artificial creada
                            para acompañar a las mujeres durante el embarazo,
                            el parto, el posparto, la lactancia y la crianza
                            temprana.
                        </p>
                        <p>
                            Su objetivo no es sustituir a las matronas ni a
                            los profesionales sanitarios. Su objetivo es
                            ofrecer información basada en evidencia científica
                            y acompañamiento respetuoso cuando surgen dudas,
                            inquietudes o momentos de incertidumbre.
                        </p>
                        <p>
                            Porque muchas preguntas aparecen cuando los
                            centros sanitarios están cerrados, cuando es de
                            madrugada, o cuando simplemente necesitamos
                            sentirnos acompañadas.
                        </p>
                    </div>
                </div>
            </section>

            {/* ¿POR QUÉ NACE? */}
            <section
                data-testid="why-section"
                className="px-6 sm:px-8 py-20 sm:py-28 bg-[#f7efe6]/55"
            >
                <div className="max-w-5xl mx-auto">
                    <BotanicalDivider label="Origen" />
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="headline text-3xl sm:text-4xl lg:text-5xl">
                            Porque ninguna mujer debería
                            <em className="italic text-[#b7677a] font-light">
                                {" "}
                                sentirse sola.
                            </em>
                        </h2>
                        <div className="mt-8 space-y-5 text-[1.02rem] leading-relaxed text-[#5c5450]">
                            <p>Matrona IA nace de una convicción sencilla:</p>
                            <p className="font-serif text-[1.35rem] italic text-[#2d2a28] leading-snug">
                                ninguna mujer debería afrontar sola sus dudas,
                                sus decisiones o sus momentos de
                                incertidumbre.
                            </p>
                            <p>
                                La maternidad puede ser una experiencia
                                profundamente transformadora, pero también una
                                etapa llena de preguntas.
                            </p>
                            <p>
                                Matrona IA nace para ofrecer un espacio seguro,
                                respetuoso y accesible donde encontrar
                                orientación basada en evidencia científica sin
                                perder la dimensión humana del acompañamiento.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CARTA DE BIENVENIDA */}
            <section
                data-testid="letter-section"
                className="px-6 sm:px-8 py-20 sm:py-28"
            >
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-10">
                        <p className="eyebrow">Carta abierta</p>
                        <h2 className="headline mt-3 text-3xl sm:text-4xl">
                            Una carta para ti
                        </h2>
                    </div>

                    <article
                        data-testid="welcome-letter"
                        className="paper rounded-3xl border border-[#e8dcc8]/70 px-7 sm:px-12 py-12 sm:py-14 shadow-[0_20px_60px_-30px_rgba(45,42,40,0.18)]"
                    >
                        <p className="font-serif italic text-[#b7677a] text-lg mb-6">
                            Hola.
                        </p>
                        <div className="space-y-5 text-[1.04rem] leading-[1.75] text-[#3c3531]">
                            <p className="dropcap font-serif text-[1.08rem]">
                                Si estás leyendo estas líneas, quiero darte la
                                bienvenida.
                            </p>
                            <p>
                                Matrona IA nace con un sueño muy sencillo: que
                                ninguna mujer tenga que atravesar sola las
                                dudas, decisiones y emociones que acompañan a
                                la maternidad.
                            </p>
                            <p>
                                Aquí encontrarás información basada en
                                evidencia científica, pero también algo igual
                                de importante:{" "}
                                <span className="italic text-[#b7677a]">
                                    acompañamiento
                                </span>
                                .
                            </p>
                            <p>
                                Porque detrás de cada pregunta hay una
                                historia, una familia y una mujer intentando
                                tomar la mejor decisión posible para ella y
                                para su bebé.
                            </p>
                            <p>
                                Mi compromiso es ofrecerte información
                                rigurosa, respeto por tus decisiones y una
                                presencia cercana cuando necesites
                                orientación.
                            </p>
                            <p>
                                Gracias por permitirme acompañarte en este
                                camino.
                            </p>
                        </div>
                        <div className="mt-10 pt-6 border-t border-[#e8dcc8]/70">
                            <p className="font-serif italic text-[#5c5450]">
                                Con cariño,
                            </p>
                            <p className="font-serif italic text-2xl text-[#b7677a] mt-1">
                                Matrona IA.
                            </p>
                        </div>
                    </article>
                </div>
            </section>

            {/* PRINCIPIOS */}
            <section
                data-testid="principles-section"
                className="px-6 sm:px-8 py-20 sm:py-28 bg-[#f7efe6]/55"
            >
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-14">
                        <p className="eyebrow">Lo que nos guía</p>
                        <h2 className="headline mt-3 text-3xl sm:text-4xl lg:text-5xl">
                            Nuestros principios
                        </h2>
                        <p className="mt-5 text-[#5c5450] max-w-xl mx-auto">
                            Ocho compromisos que sostienen cada conversación,
                            cada respuesta, cada cuidado.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
                        {PRINCIPLES.map((p, idx) => {
                            const Icon = p.icon;
                            return (
                                <div
                                    key={p.title}
                                    data-testid={`principle-card-${idx}`}
                                    className="card-soft p-6 sm:p-7 flex flex-col"
                                >
                                    <span
                                        className="w-11 h-11 rounded-full flex items-center justify-center mb-5"
                                        style={{
                                            background:
                                                "linear-gradient(135deg, #f4dee2 0%, #fdfaf6 100%)",
                                        }}
                                    >
                                        <Icon
                                            size={20}
                                            className="text-[#b7677a]"
                                            strokeWidth={1.5}
                                        />
                                    </span>
                                    <h3 className="font-serif text-[1.3rem] leading-snug text-[#2d2a28] mb-2">
                                        {p.title}
                                    </h3>
                                    <p className="text-[0.92rem] leading-relaxed text-[#5c5450]">
                                        {p.text}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* DESCARGA LA APP */}
            <section
                data-testid="download-section"
                className="relative px-6 sm:px-8 py-20 sm:py-28 overflow-hidden"
            >
                <div
                    className="absolute inset-0 opacity-60"
                    style={{
                        background:
                            "radial-gradient(ellipse at top, rgba(244,222,226,0.55) 0%, transparent 60%), radial-gradient(ellipse at bottom right, rgba(168,185,160,0.18) 0%, transparent 55%)",
                    }}
                    aria-hidden="true"
                />
                <div className="relative max-w-3xl mx-auto text-center">
                    <p className="eyebrow">La app</p>
                    <h2 className="headline mt-3 text-3xl sm:text-4xl lg:text-5xl">
                        Lleva Matrona IA
                        <em className="italic text-[#b7677a] font-light">
                            {" "}
                            contigo.
                        </em>
                    </h2>
                    <p className="mt-5 text-[#5c5450] max-w-xl mx-auto">
                        En tu bolsillo, cuando lo necesites. Ya disponible en
                        Google Play y App Store.
                    </p>
                    <div className="mt-10 flex justify-center">
                        <DownloadButtons />
                    </div>
                    <p className="mt-6 text-[0.85rem] text-[#8a807b]">
                        Matrona IA ya está disponible. Descárgala en Google
                        Play o App Store y llévala contigo cuando la
                        necesites.
                    </p>
                </div>
            </section>
        </div>
    );
}
